import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import SiteShell from "../site/SiteShell";
import PageHero from "../site/PageHero";
import { Reveal } from "../site/motion";
import { JOURNEY } from "../site/content";

export default function About() {
  return (
    <SiteShell>
      <PageHero
        overline="About Verified Workforce"
        title="Bridging the gap between capability and opportunity."
        kicker="Verified Workforce is a construction supply-chain and procurement business focused on identifying, developing and connecting capable specialist contractors with larger construction opportunities."
        image="https://images.unsplash.com/photo-1517232115160-ff93364542dd?auto=format&fit=crop&w=1800&q=70"
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/contact" className="vw-btn vw-btn--primary" data-testid="about-contact-cta">
            Work with us <ArrowUpRight size={16}/>
          </Link>
          <Link to="/delivery-partners#apply" className="vw-btn vw-btn--gold">
            Join the Network
          </Link>
        </div>
      </PageHero>

      <section className="vw-section bg-white">
        <div className="vw-container grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="vw-overline mb-4">Who We Are</div>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05]">
                A different kind of supply-chain business.
              </h2>
              <p className="mt-6 text-base lg:text-lg text-[var(--vw-charcoal)] leading-relaxed">
                We believe many excellent local businesses are prevented from accessing
                major contracts not because they lack construction capability, but because
                they lack the compliance infrastructure, commercial systems or cash-flow
                support required by larger organisations.
              </p>
              <p className="mt-4 text-base lg:text-lg text-[var(--vw-charcoal)] leading-relaxed">
                Verified Workforce exists to bridge that gap — identifying capable
                businesses, helping them become contract-ready, connecting them with
                larger opportunities and supporting them through mobilisation and delivery.
              </p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-sm text-[var(--vw-charcoal)]">
                {[
                  "Not a recruitment agency",
                  "Not a labour-only provider",
                  "Trusted supply chain, not a job board",
                  "Based in Bromley, operating UK-wide",
                ].map((x) => (
                  <li key={x} className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--vw-blue)] mt-0.5"/>{x}</li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.08}>
              <div className="relative aspect-[4/5] bg-[var(--vw-navy)] overflow-hidden">
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-70"
                  style={{ backgroundImage: "url(https://images.unsplash.com/photo-1517638851339-a711cfcf3279?auto=format&fit=crop&w=1200&q=80)" }}
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,36,0.1), rgba(10,15,36,0.85))" }} />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <div className="font-mono text-[11px] tracking-[0.22em] text-[var(--vw-gold)] uppercase">Est. 2025</div>
                  <div className="mt-2 font-display text-2xl lg:text-3xl font-semibold">Bromley · United Kingdom</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Journey recap */}
      <section className="vw-section bg-[var(--vw-paper)]">
        <div className="vw-container">
          <Reveal>
            <div className="vw-overline mb-4">The Model</div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05] max-w-3xl">
              Identify, develop, verify, connect, support, deliver.
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {JOURNEY.map((j, i) => (
              <Reveal key={j.title} delay={i*0.06}>
                <div className="vw-card h-full p-7">
                  <div className="font-mono text-[11px] tracking-[0.22em] text-[var(--vw-blue)] uppercase">Stage 0{i+1}</div>
                  <div className="mt-4 font-display text-lg font-semibold text-[var(--vw-navy)]">{j.title}</div>
                  <p className="mt-3 text-sm text-[var(--vw-muted)]">{j.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
